import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Music, Image, FileText } from 'lucide-react';
import { useCreatePersona } from '../hooks/usePersona';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

type CreationMethod = 'vision' | 'music' | 'references' | 'manual';

const creationMethods = [
  {
    id: 'vision' as CreationMethod,
    name: 'Describe Your Vision',
    description: 'Tell us about your artistic vision and we\'ll generate a visual identity',
    icon: Sparkles,
    recommended: true,
  },
  {
    id: 'music' as CreationMethod,
    name: 'Upload Music First',
    description: 'We\'ll analyze your music to understand your style',
    icon: Music,
  },
  {
    id: 'references' as CreationMethod,
    name: 'Share References',
    description: 'Upload images and documents that define your brand',
    icon: Image,
  },
  {
    id: 'manual' as CreationMethod,
    name: 'Build Manually',
    description: 'Configure every detail yourself',
    icon: FileText,
  },
];

export default function PersonaCreate() {
  const navigate = useNavigate();
  const { createPersona, isLoading, error } = useCreatePersona();

  const [step, setStep] = useState(1);
  const [method, setMethod] = useState<CreationMethod>('vision');
  const [artistName, setArtistName] = useState('');
  const [stageName, setStageName] = useState('');
  const [vision, setVision] = useState('');

  const handleSubmit = async () => {
    const persona = await createPersona({
      artist_name: artistName,
      stage_name: stageName || undefined,
      creation_method: method,
      initial_vision: method === 'vision' ? vision : undefined,
    });

    if (persona) {
      navigate(`/admin/personas/${persona.id}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back link */}
      <Link to="/admin/personas" className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200">
        <ArrowLeft className="w-4 h-4" />
        Back to personas
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Create New Persona</h1>
        <p className="text-zinc-400 mt-1">Set up a new artist persona for AI video generation</p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-jelly-500' : 'bg-zinc-800'}`}
          />
        ))}
      </div>

      {error && (
        <div className="p-3 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">{error}</div>
      )}

      {/* Step 1: Choose method */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">How do you want to start?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {creationMethods.map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`p-4 rounded-xl border text-left transition-all ${
                  method === m.id
                    ? 'border-jelly-500 bg-jelly-500/10'
                    : 'border-zinc-800 hover:border-zinc-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${method === m.id ? 'bg-jelly-500/20' : 'bg-zinc-800'}`}>
                    <m.icon className={`w-5 h-5 ${method === m.id ? 'text-jelly-400' : 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium">
                      {m.name}
                      {m.recommended && (
                        <span className="ml-2 text-xs text-jelly-400 font-normal">Recommended</span>
                      )}
                    </p>
                    <p className="text-sm text-zinc-400 mt-1">{m.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setStep(2)}>Continue</Button>
          </div>
        </div>
      )}

      {/* Step 2: Basic info */}
      {step === 2 && (
        <Card>
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Artist Name *</label>
              <input
                type="text"
                value={artistName}
                onChange={(e) => setArtistName(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-jelly-500"
                placeholder="Your artist or band name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Stage Name (optional)</label>
              <input
                type="text"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-jelly-500"
                placeholder="A shorter name or alias"
              />
            </div>
          </div>
          <div className="flex justify-between pt-6">
            <Button variant="ghost" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={() => setStep(3)} disabled={!artistName}>
              Continue
            </Button>
          </div>
        </Card>
      )}

      {/* Step 3: Vision or finalize */}
      {step === 3 && (
        <Card>
          {method === 'vision' ? (
            <>
              <h2 className="text-lg font-semibold mb-4">Describe Your Vision</h2>
              <p className="text-zinc-400 text-sm mb-4">
                Tell us about your artistic vision. What emotions, colors, and visuals represent you?
              </p>
              <textarea
                value={vision}
                onChange={(e) => setVision(e.target.value)}
                className="w-full h-40 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-jelly-500 resize-none"
                placeholder="Example: I make dreamy indie folk music with lyrics about nature and nostalgia. I want visuals that feel like golden hour in autumn - warm oranges, soft focus, falling leaves. Think Bon Iver meets Sufjan Stevens..."
              />
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold mb-4">Ready to Create</h2>
              <p className="text-zinc-400">
                Your persona <strong className="text-white">{artistName}</strong> will be created using the{' '}
                <strong className="text-white">{method}</strong> method.
              </p>
              {method === 'music' && (
                <p className="text-zinc-400 mt-2">You'll be able to upload tracks on the next screen.</p>
              )}
            </>
          )}
          <div className="flex justify-between pt-6">
            <Button variant="ghost" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button onClick={handleSubmit} isLoading={isLoading}>
              Create Persona
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
